import React from 'react';
import { Users } from 'lucide-react';
import CoachAvatar from './CoachAvatar';
import { cn } from '../../lib/utils';

/**
 * The public booking page's coach chooser (#758).
 *
 * `coaches` are the page's public coach cards (`GET /public/{slug}` →
 * `coaches`, keyed by `coach_profile_id`). `value` is the chosen id, or null
 * for "Any available coach". `rows` is the ordered list `{ id, available, hint }`
 * the page derives from the chosen date/time; `anyHint` labels the Any row.
 *
 * Accessibility: a `role="group"` of real buttons with `aria-pressed`. A row
 * the guest cannot pick right now is `aria-disabled` — NOT `disabled` — so it
 * stays focusable and its reason ("Busy at 10:00 AM", "Not on this day"),
 * which is visible text inside the button, is part of its accessible name.
 * Its click is a no-op. The muted style is never the only signal.
 *
 * A `pending` row (the chosen day's times are still loading) is also
 * `aria-disabled`, with "Checking times…" as its reason, so no coach is picked
 * for a day they may have no times on.
 *
 * The `role="group"` takes a forwarded ref and `tabIndex=-1`, so the page can
 * move focus to it (after a 409 coach_unavailable, with `describedBy` naming
 * the message).
 *
 * `layout="list"` is the ≥1024px column; `layout="chips"` is the same buttons
 * as one horizontally scrolling row for narrower widths and embeds.
 */
const CoachPicker = React.forwardRef(function CoachPicker({
    coaches = [], value = null, rows, anyHint = '', onChange, layout = 'list', brandColor = '#1a1a1a', describedBy,
}, ref) {
    const byId = new Map(coaches.map((c) => [c.coach_profile_id, c]));
    const ordered = rows || coaches.map((c) => ({ id: c.coach_profile_id, available: true, hint: '' }));
    const chips = layout === 'chips';

    const rowClass = (selected, available, pending) => cn(
        'min-h-11 rounded-lg border text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-stone-400',
        chips ? 'flex-shrink-0 px-3 py-1.5 max-w-[15rem]' : 'w-full px-3 py-2',
        selected ? 'bg-stone-50' : 'bg-white',
        available ? 'hover:border-stone-400' : pending ? 'opacity-60 cursor-wait' : 'opacity-60 cursor-not-allowed',
        selected ? '' : 'border-stone-200',
    );
    const selectedStyle = (selected) => (selected ? { borderColor: brandColor, boxShadow: `inset 0 0 0 1px ${brandColor}` } : undefined);

    const anySelected = value == null;
    return (
        <div data-testid="coach-picker" data-layout={layout} className="min-w-0">
            <p className={cn('font-semibold', chips ? 'text-xs uppercase tracking-wider text-stone-500 mb-2' : 'text-sm text-stone-800 mb-3')}>
                Who would you like to meet?
            </p>
            <div ref={ref} role="group" aria-label="Choose a coach" tabIndex={-1} aria-describedby={describedBy}
                aria-busy={ordered.some((r) => r.pending) || undefined}
                className={cn(chips ? 'flex gap-2 overflow-x-auto pb-1 -mx-1 px-1' : 'space-y-1.5', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 rounded-lg')}>
                <button type="button" aria-pressed={anySelected} data-testid="coach-row-any"
                    onClick={() => onChange && onChange(null)}
                    className={rowClass(anySelected, true)} style={selectedStyle(anySelected)}>
                    <div className="flex items-start gap-2.5 min-w-0">
                        <span aria-hidden="true" className="h-8 w-8 rounded-full bg-stone-100 text-stone-500 flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-900 truncate">Any available coach</p>
                            {anyHint && <p className="text-xs text-stone-500 truncate" data-testid="coach-row-any-hint">{anyHint}</p>}
                        </div>
                    </div>
                </button>
                {ordered.map(({ id, available, pending, hint }) => {
                    const c = byId.get(id);
                    if (!c) return null;
                    const selected = value === id;
                    return (
                        <button key={id} type="button" aria-pressed={selected}
                            aria-disabled={available ? undefined : 'true'}
                            data-testid={`coach-row-${id}`}
                            onClick={() => { if (available && onChange) onChange(id); }}
                            className={rowClass(selected, available, pending)} style={selectedStyle(selected)}>
                            <CoachAvatar size="sm" photoUrl={c.photo_url} name={c.display_name}
                                title={chips ? '' : c.title}>
                                {hint && (
                                    <p className={cn('text-xs truncate', available ? 'text-emerald-700' : 'text-stone-500')}
                                        data-testid={`coach-hint-${id}`}>
                                        {hint}
                                    </p>
                                )}
                            </CoachAvatar>
                        </button>
                    );
                })}
            </div>
        </div>
    );
});

export default CoachPicker;
